import rulesData from '@/data/diagnosis_rules.json';

export interface DiagnosisAnswers {
  q1_track?: string;       // ai_ml | data_analytics | fullstack_web | cloud_devops | infosec
  q2_experience?: string;  // beginner | self_taught | cs_grad | junior
  q3_stack?: string;       // python_ai | sql_data | ts_react | aws_infra | sec_network
  q4_commitment?: string;  // fulltime | parttime | worker | intensive
  q5_goal?: string;        // enterprise | startup | free_refund | fast_hire
}

export interface RadarPoint {
  axis: string;
  score: number;
  marketAvg: number;
  comment: string;
}

export interface DiagnosisResult {
  trackId: string;
  trackName: string;
  trackBadge: string;
  category: string;
  overallScore: number;
  percentile: string;
  radarData: RadarPoint[];
  stats: {
    observedJobs: number;
    trainingCoursesCount: number;
    avgSatisfaction: number;
    avgEmploymentRate: number;
    avgMonthlyPensionWage: number;
    salaryBand: {
      entry: string;
      junior_3yr: string;
      senior_5yr: string;
    };
  };
  topHiringCompanies: string[];
  recommendedCerts: string[];
  curriculumRoadmap: string[];
  summary: string;
  detailAnalysis: {
    hiringInsight: string;
    salaryInsight: string;
    actionItem: string;
  };
  successLift: number;
  costKrw: number;
  elapsedMs: number;
}

export function evaluateDiagnosis(answers: DiagnosisAnswers): DiagnosisResult {
  const t0 = performance.now();

  const trackKey = (answers.q1_track || 'ai_ml') as keyof typeof rulesData.tracks;
  const track = rulesData.tracks[trackKey] || rulesData.tracks.ai_ml;

  const expKey = (answers.q2_experience || 'beginner') as keyof typeof rulesData.experience_modifiers;
  const expMod = rulesData.experience_modifiers[expKey] || rulesData.experience_modifiers.beginner;

  const commitKey = (answers.q4_commitment || 'fulltime') as keyof typeof rulesData.commitment_modifiers;
  const commitMod = rulesData.commitment_modifiers[commitKey] || rulesData.commitment_modifiers.fulltime;

  const goalKey = (answers.q5_goal || 'enterprise') as keyof typeof rulesData.goal_modifiers;
  const goalMod = rulesData.goal_modifiers[goalKey] || rulesData.goal_modifiers.enterprise;

  // Stack alignment bonus
  let stackBonus = 0;
  if (
    (trackKey === 'ai_ml' && answers.q3_stack === 'python_ai') ||
    (trackKey === 'data_analytics' && answers.q3_stack === 'sql_data') ||
    (trackKey === 'fullstack_web' && answers.q3_stack === 'ts_react') ||
    (trackKey === 'cloud_devops' && answers.q3_stack === 'aws_infra') ||
    (trackKey === 'infosec' && answers.q3_stack === 'sec_network')
  ) {
    stackBonus = 6;
  } else {
    stackBonus = 2;
  }

  // Calculate 5 Radar axes
  const clamp = (val: number, min = 35, max = 98) => Math.min(max, Math.max(min, Math.round(val)));

  const skillScore = clamp(track.radar_baseline['스킬셋'] + expMod.skill + stackBonus);
  const growthScore = clamp(track.radar_baseline['성장잠재력'] + expMod.growth + commitMod.growth);
  const fitScore = clamp(track.radar_baseline['취업적합도'] + expMod.fit + (goalMod.readiness || 0) + stackBonus);
  const readinessScore = clamp(track.radar_baseline['실무완성도'] + expMod.readiness + commitMod.readiness);
  const demandScore = clamp(track.radar_baseline['시장수요도'] + (expMod.demand || 0) + (stackBonus > 4 ? 3 : 0));

  const radarData: RadarPoint[] = [
    {
      axis: '스킬셋',
      score: skillScore,
      marketAvg: track.radar_baseline['스킬셋'],
      comment: skillScore >= 75 ? '기본 코어 스택 이해도가 높습니다.' : '실습 중심의 코딩 체화가 필요합니다.'
    },
    {
      axis: '성장잠재력',
      score: growthScore,
      marketAvg: track.radar_baseline['성장잠재력'],
      comment: growthScore >= 80 ? '주간 몰입 환경이 우수하여 성장 속도가 빠릅니다.' : '정기적인 학습 루틴 구축이 권장됩니다.'
    },
    {
      axis: '취업적합도',
      score: fitScore,
      marketAvg: track.radar_baseline['취업적합도'],
      comment: fitScore >= 75 ? '목표 기업군의 채용 기준선에 부합합니다.' : '우대 자격증 취득 시 서류 합격률이 대폭 상승합니다.'
    },
    {
      axis: '실무완성도',
      score: readinessScore,
      marketAvg: track.radar_baseline['실무완성도'],
      comment: readinessScore >= 70 ? '실전 프로젝트를 수행할 기초 체력을 보유했습니다.' : '실제 상용 서비스 캡스톤 완주가 핵심 과제입니다.'
    },
    {
      axis: '시장수요도',
      score: demandScore,
      marketAvg: track.radar_baseline['시장수요도'],
      comment: '다봄 30만 건 원장 기준 지속적인 신규 구인이 발생하는 직군입니다.'
    }
  ];

  const overallScore = Math.round(
    (skillScore * 0.2) + (growthScore * 0.2) + (fitScore * 0.25) + (readinessScore * 0.2) + (demandScore * 0.15)
  );

  const rawPercentile = Math.max(2.5, 100 - (overallScore * 1.08));
  const percentile = `상위 ${rawPercentile.toFixed(1)}%`;

  const elapsedMs = Math.round((performance.now() - t0) * 100) / 100;

  return {
    trackId: track.id,
    trackName: track.name,
    trackBadge: track.badge,
    category: track.category,
    overallScore,
    percentile,
    radarData,
    stats: {
      observedJobs: track.stats.observed_job_postings,
      trainingCoursesCount: track.stats.training_courses_count,
      avgSatisfaction: track.stats.avg_satisfaction,
      avgEmploymentRate: track.stats.avg_employment_rate,
      avgMonthlyPensionWage: track.stats.avg_monthly_pension_wage,
      salaryBand: track.stats.annual_salary_band
    },
    topHiringCompanies: track.top_hiring_companies,
    recommendedCerts: track.recommended_certs,
    curriculumRoadmap: track.curriculum_roadmap,
    summary: track.summary,
    detailAnalysis: {
      hiringInsight: track.detail_analysis?.hiring_insight || '',
      salaryInsight: track.detail_analysis?.salary_insight || '',
      actionItem: track.detail_analysis?.action_item || ''
    },
    successLift: commitMod.success_lift || 20,
    costKrw: 0,
    elapsedMs
  };
}
